/**
 * WordPress dependencies
 */
import { useMemo } from '@safe-wordpress/element';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import clsx from 'clsx';

/**
 * Internal dependencies
 */
import { CustomSelectControl } from '../../../components';
import { getFontStylesAndWeights, type FontFace } from '../../utils';

/**
 * Adjusts font appearance field label in case either font styles or weights
 * are disabled.
 *
 * @param {boolean} hasFontStyles  Whether font styles are enabled and present.
 * @param {boolean} hasFontWeights Whether font weights are enabled and present.
 * @return {string} A label representing what font appearance is being edited.
 */
const getFontAppearanceLabel = (
	hasFontStyles: boolean,
	hasFontWeights: boolean
) => {
	if ( ! hasFontStyles ) {
		return _x( 'Font weight', 'text', 'nelio-ab-testing' );
	}

	if ( ! hasFontWeights ) {
		return _x( 'Font style', 'text', 'nelio-ab-testing' );
	}

	return _x( 'Appearance', 'text', 'nelio-ab-testing' );
};

/**
 * Control to display font style and weight options of the active font.
 *
 * @param {Object} props Component props.
 *
 * @return {Element} Font appearance control.
 */
type FontAppearanceControlProps = {
	readonly __next40pxDefaultSize?: boolean;
	readonly hasFontStyles?: boolean;
	readonly hasFontWeights?: boolean;
	readonly fontFamilyFaces: Array< FontFace >;
	readonly value: {
		readonly fontStyle?: string;
		readonly fontWeight?: string;
	};
	readonly onChange: ( value: {
		fontStyle?: string;
		fontWeight?: string;
	} ) => void;
	readonly size?: string;
	readonly options?: Array< {
		name?: string;
		value?: string;
	} >;
	readonly disabled?: boolean;
};
export function FontAppearanceControl(
	props: FontAppearanceControlProps
): JSX.Element | false {
	const {
		/** Start opting into the larger default height that will become the default size in a future version. */
		__next40pxDefaultSize = false,
		onChange,
		hasFontStyles = true,
		hasFontWeights = true,
		fontFamilyFaces,
		value: { fontStyle, fontWeight },
		disabled,
		...otherProps
	} = props;
	const hasStylesOrWeights = hasFontStyles || hasFontWeights;
	const label = getFontAppearanceLabel( hasFontStyles, hasFontWeights );

	const { fontStyles, fontWeights, combinedStyleAndWeightOptions } =
		getFontStylesAndWeights( fontFamilyFaces );

	// Generates select options for combined font styles and weights.
	const combineOptions = () => combinedStyleAndWeightOptions ?? [];

	// Generates select options for font styles only.
	const styleOptions = () =>
		fontStyles
			.filter(
				( fs ): fs is { name: string; value: string } =>
					!! fs.name && !! fs.value
			)
			.map( ( { name, value } ) => ( {
				key: value,
				name,
				style: { fontStyle: value, fontWeight: undefined },
			} ) );

	// Generates select options for font weights only.
	const weightOptions = () =>
		fontWeights
			.filter(
				( fs ): fs is { name: string; value: string } =>
					!! fs.name && !! fs.value
			)
			.map( ( { name, value } ) => ( {
				key: value,
				name,
				style: { fontStyle: undefined, fontWeight: value },
			} ) );

	// Map font styles and weights to select options.
	const selectOptions = useMemo( () => {
		// Display combined available font style and weight options.
		if ( hasFontStyles && hasFontWeights ) {
			return combineOptions();
		}

		// Display only font style options or font weight options.
		return hasFontStyles ? styleOptions() : weightOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		props.options,
		fontStyles,
		fontWeights,
		combinedStyleAndWeightOptions,
	] );

	// Find current selection by comparing font style & weight against options,
	// and fall back to the Default option if there is no matching option.
	const currentSelection =
		selectOptions.find(
			( option ) =>
				option.style.fontStyle === fontStyle &&
				option.style.fontWeight === fontWeight
		) || selectOptions[ 0 ];

	// Adjusts screen reader description based on styles or weights.
	const getDescribedBy = () => {
		if ( ! currentSelection ) {
			return _x(
				'No selected font appearance',
				'text',
				'nelio-ab-testing'
			);
		}

		if ( ! hasFontStyles ) {
			return sprintf(
				/* translators: %s: Currently selected font weight. */
				_x(
					'Currently selected font weight: %s',
					'text',
					'nelio-ab-testing'
				),
				currentSelection.name
			);
		}

		if ( ! hasFontWeights ) {
			return sprintf(
				/* translators: %s: Currently selected font style. */
				_x(
					'Currently selected font style: %s',
					'text',
					'nelio-ab-testing'
				),
				currentSelection.name
			);
		}

		return sprintf(
			/* translators: %s: Currently selected font appearance. */
			_x(
				'Currently selected font appearance: %s',
				'text',
				'nelio-ab-testing'
			),
			currentSelection.name
		);
	};

	return (
		hasStylesOrWeights && (
			<CustomSelectControl
				{ ...otherProps }
				disabled={ disabled }
				className={ clsx( 'components-font-appearance-control', {
					'nab-components--disabled': disabled,
				} ) }
				__next40pxDefaultSize={ __next40pxDefaultSize }
				size="default"
				label={ label }
				describedBy={ getDescribedBy() }
				options={ selectOptions }
				value={ currentSelection }
				onChange={ ( {
					selectedItem,
				}: {
					selectedItem: {
						key: string;
						name: string;
						style: {
							fontStyle?: string;
							fontWeight?: string;
						};
					};
				} ) => onChange( selectedItem.style ) }
			/>
		)
	);
}
